<template>
  <div>
    <div
      v-if="showLoadingOverlay"
      class="fullview-loading-overlay d-flex flex-column justify-content-center align-items-center"
    >
      <b-spinner variant="primary" style="width: 3rem; height: 3rem" class="mb-4" />
      <h5 class="fw-500 mb-2">
        {{ lang !== 'ENG' ? '보고서를 준비하고 있습니다.' : 'Preparing report...' }}
      </h5>
      <p class="text-muted mb-3">
        {{
          lang !== 'ENG' ? '보고서에 필요한 정보를 불러오고 있습니다.' : 'Loading report data...'
        }}
      </p>
      <b-progress
        :max="reportDataTotal"
        :value="displayedCount"
        variant="primary"
        animated
        style="width: 240px"
        height="6px"
      />
      <p class="mt-2 text-muted f-85">{{ displayedCount }} / {{ reportDataTotal }}</p>
    </div>

    <template v-else-if="reportCodeMatched">
      <StoryBoardViewer v-if="selected_report?.story_board" />
      <StandardIndex v-else />
    </template>
  </div>
</template>
<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import StoryBoardViewer from '../story_board/StoryBoardViewer';
import StandardIndex from './StandardIndex';
import ChannelService from '@/components/ChannelService';
import { read_mixin } from '@/mixins/read_mixin';

export default {
  name: 'FullView2021',
  mixins: [read_mixin],
  props: {},
  computed: {
    ...mapGetters('report_list', ['selected_report', 'current_report_code']),
    ...mapGetters('story_board', ['sb_toc']),
    ...mapState('report_list', ['lang', 'view_mode']),
    ...mapState('report_contents', ['theme_option']),
    ...mapState('documents', ['document_list_loaded']),
    ...mapState('data_tables', ['table_list_loaded']),
    ...mapState('business_cases', ['business_case_loaded']),
    ...mapState('gri2021_report_view', ['report_contents_loaded']),
    ...mapState('assurance', ['assurance_list_loaded']),
    reportCodeMatched() {
      return (
        this.selected_report?.report_code === this.current_report_code ||
        this.selected_report?.report_url === this.current_report_code
      );
    },
    reportDataCount() {
      return (
        (this.document_list_loaded ? 1 : 0) +
        (this.table_list_loaded ? 1 : 0) +
        (this.business_case_loaded ? 1 : 0) +
        (this.report_contents_loaded ? 1 : 0) +
        (this.assurance_list_loaded ? 1 : 0)
      );
    },
    reportDataTotal() {
      return 5;
    },
    showLoadingOverlay() {
      if (this.is_viewer) return false;
      return (
        !this.reportCodeMatched || this.displayedCount < this.reportDataTotal || this.previewLoading
      );
    },
    isSvg() {
      return this.img_src && this.img_src.toLowerCase().endsWith('.svg');
    }
  },
  components: {
    StoryBoardViewer,
    StandardIndex
  },
  data() {
    return {
      img_src: '',
      qesg_image_modal: false,
      originalFontSize: '',
      displayedCount: 0,
      animationTarget: 0,
      animationTimer: null,
      previewLoading: false
    };
  },
  beforeRouteEnter(to, from, next) {
    // console.log(`🔍 [FullView2021 beforeRouteEnter] to: ${to.path}, from.name: ${from.name}`);
    if (to.path.includes('/report_preview') && !from.name) {
      const navigationType = performance.getEntriesByType('navigation')[0].type;
      // console.log(`🔍 [FullView2021 beforeRouteEnter] navigationType: ${navigationType}`);

      if (
        navigationType !== 'reload' &&
        (document.referrer === '' || document.referrer.indexOf(window.location.origin) !== 0)
      ) {
        // console.log('🚫 [FullView2021 beforeRouteEnter] 외부 접근 차단 → redirect');
        window.alert('URL을 통한 페이지 접근은 제한됩니다.');
        window.location.href = 'https://report.qesg.co.kr/';
      } else {
        // console.log('✅ [FullView2021 beforeRouteEnter] next() 호출');
        next();
      }
    } else {
      // console.log('✅ [FullView2021 beforeRouteEnter] 조건 미해당 → next()');
      next();
    }
  },

  watch: {
    'selected_report.font_size'(newSize) {
      if (newSize !== undefined && newSize !== null) {
        document.documentElement.style.fontSize = `${newSize}px`;
      }
    },
    reportDataCount: {
      immediate: true,
      handler(newVal) {
        this.animationTarget = newVal;
        this.animateProgress();
      }
    }
  },
  methods: {
    ...mapActions('story_board', [
      'fetch_sb_toc',
      'fetch_main_setting_from_viewer',
      'fetch_viewer_sb_disclosures'
    ]),
    animateProgress() {
      if (this.displayedCount >= this.animationTarget) return;
      this.animationTimer = setTimeout(() => {
        this.displayedCount++;
        if (this.displayedCount < this.animationTarget) {
          this.animateProgress();
        }
      }, 600);
    },
    async fetchDisclosuresForRestoredLv() {
      // fetch_sb_toc 내부에서 URL 쿼리 파라미터로 lv1/lv2를 이미 복원하므로,
      // 여기서는 복원된 lv1/lv2 기반으로 disclosure만 fetch한다.
      const lv1 = this.$store.getters['story_board/selected_lv1'];
      const lv2 = this.$store.getters['story_board/selected_lv2'];
      if (!lv1 || !lv2) return;

      await this.fetch_viewer_sb_disclosures({
        report_code: this.current_report_code,
        selected_lv_info: { lv1_info: lv1, lv2_info: lv2 }
      });
    },
    async loadPreviewData() {
      const reportCode = this.current_report_code;
      if (!reportCode) return;

      const hasBaseData = this.theme_option && this.sb_toc.length > 0;

      if (!hasBaseData) {
        this.previewLoading = true;
      }

      try {
        if (!hasBaseData) {
          await this.get_theme_info({ report_code: reportCode });
          await this.readReport(reportCode);
          await this.readErtContent(reportCode);
          await this.readLegacyReport(reportCode);
          await this.readAssurance(reportCode);
          await this.fetch_sb_toc({ report_code: reportCode, select_lv: false });
          await this.initializeCSS();
        }

        // 미리보기/PDF에 필요한 데이터는 항상 로드
        if (!this.$store.state.story_board.main_setting) {
          await this.fetch_main_setting_from_viewer({ report_code: reportCode });
        }
        if (!this.$store.state.assurance.assurance_list_loaded) {
          this.readAssuranceListByReportCode({ report_code: reportCode });
        }
        // viewer_disclosure_map은 PDF 출력에 필수이므로 항상 로드
        await this.fetch_viewer_sb_disclosures({ report_code: reportCode });
        await this.fetchDisclosuresForRestoredLv();
      } catch (e) {
        console.error('[FullView2021] 미리보기 데이터 로딩 실패:', e);
      } finally {
        this.previewLoading = false;
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      const overlay = document.getElementById('page-transition-overlay');
      if (overlay) overlay.remove();
    });
    if (!process.env.VUE_APP_REPORT_CODE) {
      ChannelService.hideChannelButton();
    }
    this.originalFontSize =
      document.documentElement.style.fontSize ||
      window.getComputedStyle(document.documentElement).fontSize;
    if (
      this.selected_report &&
      this.selected_report.font_size !== undefined &&
      this.selected_report.font_size !== null
    ) {
      document.documentElement.style.fontSize = `${this.selected_report.font_size}px`;
    }

    if (this.$route.path.includes('/report_preview')) {
      this.loadPreviewData();
    }
  },
  beforeDestroy() {
    clearTimeout(this.animationTimer);
    document.documentElement.style.fontSize = this.originalFontSize;
    ChannelService.boot();
    this.$store.commit('report_list/change_lang', 'KOR');
  },
  created() {
    if (this.$route.query?.l == 'ENG') {
      this.$store.commit('report_list/change_lang', 'ENG');
    } else {
      this.$store.commit('report_list/change_lang', 'KOR');
    }
  }
};
</script>

<style scoped>
.fullview-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  z-index: 9999;
}
</style>
